"use client";

import { useEffect, useState } from "react";
import Script from "next/script";

// ----------------------------------------------------------------------------
// Mandatly cookie banner integration.
//
// Loading is gated on first user interaction OR a 4s fallback so the banner
// script is never on the critical path. Saves ~250-400ms TBT on mobile
// because the third-party script (cdnscript.mandatlyonline.com) used to run
// synchronously after hydration and inflated INP/TBT measurably.
//
// Consent flips in-place via `__cbConsentUpdate(boolean)` (see
// GoogleTagManagerScript.tsx) — no reload needed.
// ----------------------------------------------------------------------------

const ACCEPT_EVENTS = [
  "MandatlyCookieOnAcceptAll",
  "MandatlyCookieOnAcceptSelected",
];
const DECLINE_EVENTS = ["MandatlyCookieOnDeclineAll"];

const MandatlyCookieScript = () => {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setShouldLoad(true);
    };

    const interactionEvents = [
      "pointerdown",
      "keydown",
      "scroll",
      "touchstart",
    ];
    interactionEvents.forEach((evt) =>
      window.addEventListener(evt, trigger, { once: true, passive: true }),
    );
    const fallback = window.setTimeout(trigger, 4000);

    return () => {
      interactionEvents.forEach((evt) =>
        window.removeEventListener(evt, trigger),
      );
      window.clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    type ConsentWindow = Window & {
      __cbConsentUpdate?: (accepted: boolean) => void;
    };
    const w = window as ConsentWindow;

    const handleAccept = () => w.__cbConsentUpdate?.(true);
    const handleDecline = () => w.__cbConsentUpdate?.(false);

    ACCEPT_EVENTS.forEach((event) =>
      window.addEventListener(event, handleAccept),
    );
    DECLINE_EVENTS.forEach((event) =>
      window.addEventListener(event, handleDecline),
    );

    return () => {
      ACCEPT_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleAccept),
      );
      DECLINE_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleDecline),
      );
    };
  }, []);

  if (!shouldLoad) return null;

  return (
    <Script
      id="mandatlycookie"
      src="https://cdnscript.mandatlyonline.com/cap/bn/d0e1c4e9-23d2-470c-a428-499a7d744396.js"
      strategy="lazyOnload"
    />
  );
};

export default MandatlyCookieScript;

// Production URL
// <!-- Start mandatly banner -->
// <script type='text/javascript' id='mandatlycookie' src='https://cdnscript.mandatlyonline.com/cap/bn/d0e1c4e9-23d2-470c-a428-499a7d744396.js'></script>
// <!-- End mandatly banner -->

// Staging URL
// <!-- Start mandatly banner -->
// <script type='text/javascript' id='mandatlycookie' src='https://cdnscript.mandatlyonline.com/cap/bn/43b2d263-c50c-47e9-a154-90c476aee40b.js'></script>
// <!-- End mandatly banner -->
